import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { TemplateService } from '../service/template.service';
import { MarketingFormsService } from '../service/marketing-forms.service';
import { extractApiErrorMessage } from '../utils/api-error.util';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { FormModalComponent } from '../components/form-modal/form-modal.component';
import { FormTypeLabelPipe } from '../pipes/form-type-label.pipe';

import { TemplateModel } from '../models/template.model';
import { FormCreatePayload, FormType } from '../models/form.model';

interface TypeFilter {
  value: FormType | 'all';
  label: string;
}

@Component({
  selector: 'app-templates-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ModalComponent, FormModalComponent, FormTypeLabelPipe],
  templateUrl: './templates-list.component.html',
})
export class TemplatesListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  templates: TemplateModel[] = [];
  loading = false;
  cloning = false;
  isCreateModalOpen = false;
  isCreating = false;

  activeFilter: FormType | 'all' = 'all';
  selectedTemplate: TemplateModel | null = null;
  newFormName = '';
  nameDialogOpen = false;

  // inc24: modal de detalle de plantilla
  detailTemplate: TemplateModel | null = null;

  readonly typeFilters: TypeFilter[] = [
    { value: 'all', label: 'Todos' },
    { value: 'INLINE', label: 'Incrustado' },
    { value: 'POPUP', label: 'Pop-up' },
    { value: 'STICKY_BAR', label: 'Barra fija' },
    { value: 'SLIDE_IN', label: 'Deslizante' },
  ];

  readonly skeletonItems = Array(8);

  constructor(
    private templateService: TemplateService,
    private formsService: MarketingFormsService,
    private router: Router,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    public datetime: DatetimeService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    const type = this.activeFilter !== 'all' ? this.activeFilter : undefined;
    this.templateService.getTemplates(type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.templates = templates;
          this.loading = false;
        },
        error: err => {
          this.toast.error(extractApiErrorMessage(err, 'No se pudieron cargar las plantillas'));
          this.loading = false;
        },
      });
  }

  onCreateTemplate(payload: FormCreatePayload): void {
    this.isCreating = true;
    this.formsService.createForm(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: form => {
          this.isCreating = false;
          this.isCreateModalOpen = false;
          this.toast.created('Plantilla');
          this.router.navigate(['/marketing-forms', form.id, 'editor']);
        },
        error: err => {
          this.isCreating = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo crear la plantilla'));
        },
      });
  }

  setFilter(filter: FormType | 'all'): void {
    if (this.activeFilter === filter) return;
    this.activeFilter = filter;
    this.load();
  }

  editTemplate(id: number): void {
    this.router.navigate(['/marketing-forms', id, 'editor']);
  }

  // ── Detalle (inc24) ─────────────────────────────────────────────────────────

  openDetail(template: TemplateModel): void {
    this.detailTemplate = template;
  }

  closeDetail(): void {
    this.detailTemplate = null;
  }

  // ── Archivar / eliminar (inc24) ─────────────────────────────────────────────

  async archiveTemplate(template: TemplateModel): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Archivar plantilla',
      message: `¿Archivar la plantilla "${template.name}"? Dejará de aparecer disponible para crear formularios.`,
      confirmText: 'Archivar',
    });
    if (!ok) return;
    this.formsService.archiveForm(template.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.toast.success('Plantilla archivada'); this.load(); },
        error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo archivar la plantilla')),
      });
  }

  async deleteTemplate(template: TemplateModel): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Eliminar la plantilla "${template.name}"? Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.formsService.deleteForm(template.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.toast.deleted('Plantilla'); this.closeDetail(); this.load(); },
        error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo eliminar la plantilla')),
      });
  }

  statusLabel(status?: string): string {
    switch (status) {
      case 'DRAFT': return 'Borrador';
      case 'PUBLISHED': return 'Publicada';
      case 'PAUSED': return 'Pausada';
      case 'ARCHIVED': return 'Archivada';
      default: return status ?? '—';
    }
  }

  statusBadgeClass(status?: string): string {
    switch (status) {
      case 'PUBLISHED': return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'DRAFT':     return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400';
      case 'PAUSED':    return 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400';
      case 'ARCHIVED':  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
      default:          return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  }

  openNameDialog(template: TemplateModel): void {
    this.selectedTemplate = template;
    this.newFormName = template.name;
    this.nameDialogOpen = true;
  }

  closeNameDialog(): void {
    this.nameDialogOpen = false;
    this.selectedTemplate = null;
    this.newFormName = '';
  }

  useTemplate(): void {
    if (!this.selectedTemplate || !this.newFormName.trim() || this.cloning) return;

    this.cloning = true;
    this.templateService
      .cloneTemplate(this.selectedTemplate.id, this.newFormName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (form) => {
          this.cloning = false;
          this.closeNameDialog();
          this.toast.created('Formulario');
          this.router.navigate(['/marketing-forms', form.id, 'editor']);
        },
        error: err => {
          this.cloning = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo crear el formulario desde la plantilla'));
        },
      });
  }

  gradientClass(type: FormType): string {
    const map: Record<FormType, string> = {
      INLINE: 'from-brand-400 to-brand-600',
      POPUP: 'from-purple-400 to-purple-600',
      STICKY_BAR: 'from-orange-400 to-orange-600',
      SLIDE_IN: 'from-emerald-400 to-emerald-600',
    };
    return map[type] ?? 'from-gray-400 to-gray-600';
  }

  badgeClass(type: FormType): string {
    const map: Record<FormType, string> = {
      INLINE: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400',
      POPUP: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
      STICKY_BAR: 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
      SLIDE_IN: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
    };
    return map[type] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }

  typeIcon(type: FormType): string {
    const icons: Record<FormType, string> = {
      INLINE: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>',
      POPUP: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 4h16v12H4V4zm4 16h8M8 16v4m8-4v4"/><rect x="7" y="7" width="10" height="6" rx="1" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>',
      STICKY_BAR: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 18h16M4 12h16"/>',
      SLIDE_IN: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 4h10v16H4V4zm10 4l6 4-6 4"/>',
    };
    return icons[type] ?? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"/>';
  }
}
